import React, { useState, useEffect, useMemo } from 'react';
import apiClient, { 
  getAccountsPayable,
  getAccountsPayableSummary,
  createPaymentRecord,
  createBulkPayment,
  getPaymentsForPurchase,
  deletePaymentRecord,
  uploadPaymentDocument,
  downloadPaymentDocument,
  deletePaymentDocument,
  getPaidInvoices
} from '../api/client';
import { useStores } from '../contexts/StoreContext';
import { formatCurrency, formatDualCurrency, formatDate } from '../utils/formatters';
import { useExchangeRate } from '../contexts/ExchangeRateContext';

// Pagination component - moved outside to avoid re-creation on each render
const PaginationControls = ({ data, onPageChange, itemsPerPage }) => {
  if (data.totalPages <= 1) return null;
  
  return (
    <div className="flex items-center justify-between mt-4 p-4 bg-gray-50 rounded">
      <div className="text-sm text-gray-600">
        Mostrando {((data.currentPage - 1) * itemsPerPage) + 1} - {Math.min(data.currentPage * itemsPerPage, data.totalItems)} de {data.totalItems} resultados
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onPageChange(data.currentPage - 1)}
          disabled={data.currentPage === 1}
          className="px-3 py-1 rounded bg-blue-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700"
        >
          ← Anterior
        </button>
        <span className="text-sm text-gray-700">
          Página {data.currentPage} de {data.totalPages}
        </span>
        <button
          onClick={() => onPageChange(data.currentPage + 1)}
          disabled={data.currentPage === data.totalPages}
          className="px-3 py-1 rounded bg-blue-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700"
        >
          Siguiente →
        </button>
      </div>
    </div>
  );
};

const AccountsPayable = () => {
  const { stores } = useStores();
  const { exchangeRate } = useExchangeRate();
  const [accountsPayable, setAccountsPayable] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({ total_debt_usd: 0, overdue_count: 0, stores_with_debt: 0 });
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    payment_amount_usd: '',
    payment_amount_ves: '',
    exchange_rate_used: '',
    payment_date: new Date(),
    payment_reference: '',
    payment_reference2: '',
    payment_reference3: '',
    payment_reference4: '',
    payment_method: '',
    notes: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('pending');
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [loadingPaid, setLoadingPaid] = useState(false);
  const [paidFilters, setPaidFilters] = useState({
    storeId: '',
    startDate: null,
    endDate: null,
    supplierName: '',
    paymentMethod: '',
    limit: 10000  // Traer todas las facturas pagadas
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20); // Mostrar 20 items por página
  const [paidCurrentPage, setPaidCurrentPage] = useState(1);
  
  // Search states
  const [searchPendingInvoice, setSearchPendingInvoice] = useState('');
  const [searchPaidInvoice, setSearchPaidInvoice] = useState('');
  
  // Estado para controlar carga inicial
  const [loadFullData, setLoadFullData] = useState(false);
  
  // Multiple selection state
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [showBulkPaymentModal, setShowBulkPaymentModal] = useState(false);
  
  // PDF viewing now handled via new tab opening instead of modal

  // Get unique suppliers from current data
  const getUniqueSuppliers = useMemo(() => {
    const suppliers = new Set();
    Object.values(accountsPayable).forEach(storeData => {
      Object.keys(storeData.suppliers || {}).forEach(supplierName => {
        suppliers.add(supplierName);
      });
    });
    return Array.from(suppliers).sort();
  }, [accountsPayable]);

  // Filter accounts payable by supplier
  const getFilteredAccountsPayable = useMemo(() => {
    if (!selectedSupplier) {
      return accountsPayable;
    }

    const filtered = {};
    Object.entries(accountsPayable).forEach(([storeId, storeData]) => {
      const filteredSuppliers = {};
      Object.entries(storeData.suppliers || {}).forEach(([supplierName, supplierData]) => {
        if (supplierName.toLowerCase().includes(selectedSupplier.toLowerCase())) {
          filteredSuppliers[supplierName] = supplierData;
        }
      });
      
      if (Object.keys(filteredSuppliers).length > 0) {
        filtered[storeId] = {
          ...storeData,
          suppliers: filteredSuppliers
        };
      }
    });
    
    return filtered;
  }, [accountsPayable, selectedSupplier]);

  // Get paginated data for pending invoices
  const getPaginatedPendingData = useMemo(() => {
    const allInvoices = [];
    
    // Flatten all invoices from all stores and suppliers
    Object.entries(getFilteredAccountsPayable).forEach(([storeId, storeData]) => {
      Object.entries(storeData.suppliers || {}).forEach(([supplierName, supplierData]) => {
        (supplierData.invoices || []).forEach(invoice => {
          allInvoices.push({
            ...invoice,
            storeId,
            storeName: storeData.store_name,
            supplierName
          });
        });
      });
    });
    
    // Filter by invoice number search
    let filteredInvoices = allInvoices;
    if (searchPendingInvoice.trim()) {
      filteredInvoices = allInvoices.filter(invoice => 
        invoice.invoice_number?.toLowerCase().includes(searchPendingInvoice.toLowerCase())
      );
    }
    
    // Sort by due date
    filteredInvoices.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
    
    // Paginate
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedItems = filteredInvoices.slice(startIndex, endIndex);
    
    return {
      items: paginatedItems,
      totalItems: filteredInvoices.length,
      totalPages: Math.ceil(filteredInvoices.length / itemsPerPage),
      currentPage
    };
  }, [getFilteredAccountsPayable, currentPage, itemsPerPage, searchPendingInvoice]);

  // Get paginated paid invoices
  const getPaginatedPaidInvoices = useMemo(() => {
    // Filter by invoice number search
    let filteredInvoices = paidInvoices;
    if (searchPaidInvoice.trim()) {
      filteredInvoices = paidInvoices.filter(invoice => 
        invoice.invoice_number?.toLowerCase().includes(searchPaidInvoice.toLowerCase())
      );
    }
    
    const startIndex = (paidCurrentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedItems = filteredInvoices.slice(startIndex, endIndex);
    
    return {
      items: paginatedItems,
      totalItems: filteredInvoices.length,
      totalPages: Math.ceil(filteredInvoices.length / itemsPerPage),
      currentPage: paidCurrentPage
    };
  }, [paidInvoices, paidCurrentPage, itemsPerPage, searchPaidInvoice]);

  const fetchAccountsPayable = async () => {
    try {
      setLoading(true);
      console.log('[AccountsPayable] Fetching accounts payable...');
      console.log('[AccountsPayable] Filters:', { selectedStore, startDate, endDate });
      
      // Si no hay tienda seleccionada y no se ha activado loadFullData, no cargar
      if (!selectedStore && !loadFullData) {
        console.log('[AccountsPayable] Skipping load - no store selected and full data not requested');
        setAccountsPayable({});
        setLoading(false);
        return;
      }
      
      const data = await getAccountsPayable(
        selectedStore || null,
        startDate ? startDate.toISOString().split('T')[0] : null,
        endDate ? endDate.toISOString().split('T')[0] : null
      );
      
      console.log('[AccountsPayable] Data received:', data);
      console.log('[AccountsPayable] Data type:', typeof data);
      console.log('[AccountsPayable] Is object:', typeof data === 'object');
      console.log('[AccountsPayable] Keys:', Object.keys(data || {}));
      
      setAccountsPayable(data);
      setError(null);
    } catch (err) {
      console.error('[AccountsPayable] ERROR DETAILS:');
      console.error('[AccountsPayable] Error message:', err.message);
      console.error('[AccountsPayable] Error response:', err.response?.data);
      console.error('[AccountsPayable] Error status:', err.response?.status);
      console.error('[AccountsPayable] Full error:', err);
      setError('Error al cargar las cuentas por pagar');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      console.log('[AccountsPayable] Fetching summary...');
      const data = await getAccountsPayableSummary();
      console.log('[AccountsPayable] Summary received:', data);
      setSummary(data);
    } catch (err) {
      console.error('[AccountsPayable] Error fetching summary:', err);
      // Don't show error for summary, just keep zeros
    }
  };

  const fetchPaidInvoices = async () => {
    try {
      setLoadingPaid(true);
      setError(null); // Clear any previous errors
      console.log('[AccountsPayable] Fetching paid invoices...');
      
      // Safely convert dates with validation
      let startDateStr = null;
      let endDateStr = null;
      
      if (paidFilters.startDate && paidFilters.startDate instanceof Date && !isNaN(paidFilters.startDate)) {
        startDateStr = paidFilters.startDate.toISOString().split('T')[0];
      }
      
      if (paidFilters.endDate && paidFilters.endDate instanceof Date && !isNaN(paidFilters.endDate)) {
        endDateStr = paidFilters.endDate.toISOString().split('T')[0];
      }
      
      const filters = {
        storeId: paidFilters.storeId || null,
        startDate: startDateStr,
        endDate: endDateStr,
        supplierName: paidFilters.supplierName || null,
        paymentMethod: paidFilters.paymentMethod || null,
        limit: paidFilters.limit
      };
      
      console.log('[AccountsPayable] Paid invoices filters:', filters);
      const data = await getPaidInvoices(filters);
      console.log('[AccountsPayable] Paid invoices data received:', data);
      setPaidInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[AccountsPayable] ERROR FETCHING PAID INVOICES:');
      console.error('[AccountsPayable] Error message:', err.message);
      console.error('[AccountsPayable] Error response:', err.response?.data);
      console.error('[AccountsPayable] Error status:', err.response?.status);
      console.error('[AccountsPayable] Full error:', err);
      setError('Error al cargar las facturas pagadas');
      setPaidInvoices([]); // Set empty array on error
    } finally {
      setLoadingPaid(false);
    }
  };

  const clearFilters = () => {
    setSelectedStore('');
    setSelectedSupplier('');
    setStartDate(null);
    setEndDate(null);
  };

  // Handle exchange rate change
  const handleExchangeRateChange = (value) => {
    const rate = parseFloat(value) || 0;
    const usdAmount = parseFloat(paymentForm.payment_amount_usd) || 0;
    const vesAmount = (usdAmount * rate).toFixed(2);
    
    setPaymentForm(prev => ({
      ...prev,
      exchange_rate_used: value,
      payment_amount_ves: vesAmount
    }));
  };

  // Handle USD amount change and auto-convert to VES using manual exchange rate
  const handleUSDAmountChange = (value) => {
    const usdAmount = parseFloat(value) || 0;
    const rate = parseFloat(paymentForm.exchange_rate_used) || 0;
    const vesAmount = (usdAmount * rate).toFixed(2);
    
    setPaymentForm(prev => ({
      ...prev,
      payment_amount_usd: value,
      payment_amount_ves: vesAmount
    }));
  };

  // Handle VES amount change and auto-convert to USD using manual exchange rate
  const handleVESAmountChange = (value) => {
    const vesAmount = parseFloat(value) || 0;
    const rate = parseFloat(paymentForm.exchange_rate_used) || 1;
    const usdAmount = (vesAmount / rate).toFixed(2);
    
    setPaymentForm(prev => ({
      ...prev,
      payment_amount_ves: value,
      payment_amount_usd: usdAmount
    }));
  };

  const handleOpenPaymentModal = (invoice) => {
    // Calculate the correct VES amount based on invoice status
    let vesAmount;
    let exchangeRateToUse;
    
    if (invoice.is_overdue && invoice.total_amount_ves_current) {
      // If invoice is overdue and has indexed amount, use the indexed amount
      vesAmount = (invoice.remaining_debt / invoice.total_amount_usd * invoice.total_amount_ves_current).toFixed(2);
      exchangeRateToUse = invoice.total_amount_ves_current / invoice.total_amount_usd;
    } else {
      // If invoice is not overdue, use original VES amount and original exchange rate
      const originalVesAmount = invoice.total_amount_ves_original || (invoice.total_amount_usd * (invoice.exchange_rate_at_purchase || exchangeRate));
      vesAmount = (invoice.remaining_debt / invoice.total_amount_usd * originalVesAmount).toFixed(2);
      exchangeRateToUse = invoice.exchange_rate_at_purchase || exchangeRate;
    }

    setSelectedInvoice(invoice);
    setPaymentForm({
      payment_amount_usd: invoice.remaining_debt.toString(),
      payment_amount_ves: vesAmount,
      exchange_rate_used: exchangeRateToUse.toFixed(2),
      payment_date: new Date(),
      payment_reference: '',
    payment_reference2: '',
    payment_reference3: '',
    payment_reference4: '',
      payment_method: '',
      notes: ''
    });
    setSelectedFile(null);
    setShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedInvoice(null);
    setPaymentForm({
      payment_amount_usd: '',
      payment_amount_ves: '',
      exchange_rate_used: '',
      payment_date: new Date(),
      payment_reference: '',
    payment_reference2: '',
    payment_reference3: '',
    payment_reference4: '',
      payment_method: '',
      notes: ''
    });
    setSelectedFile(null);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const paymentData = {
        inventory_purchase_id: selectedInvoice.id,
        payment_amount_usd: parseFloat(paymentForm.payment_amount_usd),
        payment_amount_ves: parseFloat(paymentForm.payment_amount_ves),
        exchange_rate_used: exchangeRate,
        payment_date: paymentForm.payment_date.toISOString().split('T')[0],
        payment_reference: paymentForm.payment_reference,
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes
      };

      const paymentRecord = await createPaymentRecord(paymentData);

      // Upload document if provided
      if (selectedFile) {
        try {
          const uploadResult = await uploadPaymentDocument(paymentRecord.id, selectedFile);
          console.log('Document uploaded successfully:', uploadResult);
        } catch (uploadErr) {
          console.error('Error uploading document:', uploadErr);
          alert('Pago registrado, pero hubo un error al subir el documento: ' + 
                (uploadErr.response?.data?.detail || uploadErr.message));
        }
      }

      await fetchAccountsPayable();
      handleClosePaymentModal();
      alert('Pago registrado correctamente' + (selectedFile ? ' con documento adjunto' : ''));
    } catch (err) {
      console.error('Error creating payment:', err);
      setError('Error al registrar el pago: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      alert('Por favor selecciona un archivo PDF válido');
      e.target.value = '';
    }
  };

  const handleViewPaymentHistory = async (invoice) => {
    setSelectedInvoice(invoice);
    setLoadingHistory(true);
    try {
      const history = await getPaymentsForPurchase(invoice.id);
      setPaymentHistory(history);
      setShowPaymentHistory(true);
    } catch (err) {
      console.error('Error fetching payment history:', err);
      setError('Error al cargar el historial de pagos');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDownloadDocument = async (paymentId, filename = 'payment_document.pdf') => {
    try {
      console.log('Starting document download for payment:', paymentId);
      const blob = await downloadPaymentDocument(paymentId);
      
      if (!blob || blob.size === 0) {
        throw new Error('El documento está vacío o no se pudo descargar');
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
      
      console.log('Document download initiated successfully');
    } catch (err) {
      console.error('Error downloading document:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Error desconocido';
      alert(`Error al descargar el documento: ${errorMessage}`);
    }
  };

  const handleDeleteDocument = async (paymentId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este documento?')) {
      return;
    }

    try {
      await deletePaymentDocument(paymentId);
      // Refresh payment history
      if (selectedInvoice) {
        await handleViewPaymentHistory(selectedInvoice);
      }
      alert('Documento eliminado correctamente');
    } catch (err) {
      console.error('Error deleting document:', err);
      alert('Error al eliminar el documento');
    }
  };

  const handleViewPdf = async (paymentId, filename = 'document.pdf') => {
    try {
      console.log('Attempting to download PDF for payment:', paymentId);
      const blob = await downloadPaymentDocument(paymentId);
      
      // Verify the blob is valid
      if (!blob || blob.size === 0) {
        throw new Error('El documento está vacío o no se pudo descargar');
      }
      
      // Check if it's actually a PDF
      if (blob.type && blob.type !== 'application/pdf') {
        console.warn('Warning: Downloaded file is not a PDF:', blob.type);
      }
      
      const url = window.URL.createObjectURL(blob);
      console.log('PDF URL created:', url);
      
      // Open PDF in new tab instead of modal to avoid iframe blob URL issues
      const newTab = window.open('', '_blank');
      if (newTab) {
        newTab.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Visualizar PDF - ${filename}</title>
            <style>
              body { margin: 0; padding: 0; background: #f0f0f0; }
              .pdf-container { 
                width: 100%; 
                height: 100vh; 
                display: flex; 
                flex-direction: column; 
                align-items: center; 
                justify-content: center; 
              }
              .pdf-frame { 
                width: 95%; 
                height: 95vh; 
                border: none; 
                box-shadow: 0 4px 6px rgba(0,0,0,0.1); 
                background: white;
              }
              .error-message { 
                text-align: center; 
                padding: 2rem; 
                background: white; 
                border-radius: 8px; 
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                max-width: 600px;
              }
              .download-btn {
                display: inline-block;
                background: #3b82f6;
                color: white;
                padding: 12px 24px;
                text-decoration: none;
                border-radius: 6px;
                margin-top: 16px;
                font-family: system-ui, -apple-system, sans-serif;
              }
            </style>
          </head>
          <body>
            <div class="pdf-container">
              <iframe src="${url}" class="pdf-frame" title="PDF Viewer" 
                      onerror="handleIframeError()" 
                      onload="console.log('PDF loaded successfully')">
              </iframe>
            </div>
            <script>
              function handleIframeError() {
                console.error('PDF iframe failed to load');
                document.querySelector('.pdf-container').innerHTML = \`
                  <div class="error-message">
                    <h2>📄 No se pudo mostrar el PDF</h2>
                    <p>El documento no se puede visualizar en el navegador, pero puedes descargarlo directamente.</p>
                    <a href="${url}" download="${filename}" class="download-btn">📥 Descargar PDF</a>
                  </div>
                \`;
              }
              
              // Auto-cleanup blob URL when window closes
              window.addEventListener('beforeunload', function() {
                URL.revokeObjectURL('${url}');
              });
            </script>
          </body>
          </html>
        `);
        newTab.document.close();
      } else {
        // If popup blocked, download the file directly
        console.warn('Popup blocked, downloading file directly');
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Error viewing PDF:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Error desconocido';
      alert(`Error al visualizar el documento PDF: ${errorMessage}. El documento se descargará automáticamente.`);
      
      // Fallback: try to download the file directly via backend URL
      try {
        await handleDownloadDocument(paymentId, filename);
      } catch (downloadErr) {
        console.error('Fallback download also failed:', downloadErr);
      }
    }
  };

  const getDaysUntilDueColor = (daysUntilDue) => {
    if (daysUntilDue < 0) return 'text-red-600 font-bold'; // Overdue
    if (daysUntilDue <= 7) return 'text-orange-600 font-bold'; // Due soon
    if (daysUntilDue <= 30) return 'text-yellow-600'; // Due this month
    return 'text-green-600'; // Not urgent
  };

  const getDaysUntilDueText = (daysUntilDue) => {
    if (daysUntilDue < 0) return `Vencida hace ${Math.abs(daysUntilDue)} días`;
    if (daysUntilDue === 0) return 'Vence hoy';
    if (daysUntilDue === 1) return 'Vence mañana';
    return `Vence en ${daysUntilDue} días`;
  };

  useEffect(() => {
    if (activeTab === 'pending') {
      setCurrentPage(1); // Reset to first page when filters change
      fetchAccountsPayable();
    }
  }, [activeTab, selectedStore, startDate, endDate]);

  // Load summary on mount
  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    if (activeTab === 'paid') {
      setPaidCurrentPage(1); // Reset to first page when filters change
      fetchPaidInvoices();
    }
  }, [activeTab, paidFilters.storeId, paidFilters.startDate, paidFilters.endDate, paidFilters.supplierName, paidFilters.paymentMethod]);

  const getTotalDebt = useMemo(() => {
    return Object.values(accountsPayable).reduce((total, store) => {
      return total + Object.values(store.suppliers || {}).reduce((storeTotal, supplier) => {
        return storeTotal + (supplier.total_debt || 0);
      }, 0);
    }, 0);
  }, [accountsPayable]);

  const getOverdueCount = useMemo(() => {
    let overdueCount = 0;
    Object.values(accountsPayable || {}).forEach(store => {
      Object.values(store.suppliers || {}).forEach(supplier => {
        (supplier.invoices || []).forEach(invoice => {
          if (invoice.is_overdue) overdueCount++;
        });
      });
    });
    return overdueCount;
  }, [accountsPayable]);

  // Resumen simple de deuda por proveedor
  const suppliersSummary = useMemo(() => {
    const summary = {};
    Object.values(accountsPayable || {}).forEach(storeData => {
      Object.entries(storeData.suppliers || {}).forEach(([supplierName, supplierData]) => {
        if (!summary[supplierName]) {
          summary[supplierName] = 0;
        }
        summary[supplierName] += (supplierData.total_debt || 0);
      });
    });
    // Convertir a array y ordenar por deuda descendente
    return Object.entries(summary)
      .map(([name, debt]) => ({ name, debt }))
      .sort((a, b) => b.debt - a.debt);
  }, [accountsPayable]);

  // Calcular totales de facturas seleccionadas
  const selectedTotals = useMemo(() => {
    let totalUSD = 0;
    let count = 0;
    
    Object.values(accountsPayable || {}).forEach(storeData => {
      Object.values(storeData.suppliers || {}).forEach(supplier => {
        (supplier.invoices || []).forEach(invoice => {
          if (selectedInvoices.has(invoice.id)) {
            totalUSD += invoice.remaining_debt || 0;
            count++;
          }
        });
      });
    });
    
    return { totalUSD, count };
  }, [accountsPayable, selectedInvoices]);

  // Función para alternar selección de factura
  const toggleInvoiceSelection = (invoiceId) => {
    const newSelected = new Set(selectedInvoices);
    if (newSelected.has(invoiceId)) {
      newSelected.delete(invoiceId);
    } else {
      newSelected.add(invoiceId);
    }
    setSelectedInvoices(newSelected);
  };

  // Función para seleccionar todas las facturas visibles
  const selectAllVisible = () => {
    const newSelected = new Set(selectedInvoices);
    const visibleInvoices = getPaginatedPendingData.items;
    
    visibleInvoices.forEach(invoice => {
      newSelected.add(invoice.id);
    });
    
    setSelectedInvoices(newSelected);
  };

  // Función para deseleccionar todas
  const clearSelection = () => {
    setSelectedInvoices(new Set());
  };

  if (loading && activeTab === 'pending') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Cargando cuentas por pagar...</span>
      </div>
    );
  }

  const totalDebt = getTotalDebt;
  const overdueCount = getOverdueCount;

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">💳 Cuentas por Pagar</h1>
          <p className="text-gray-600 mt-1">
            🆕 NUEVA VERSIÓN CON PESTAÑAS - Gestión de pagos a proveedores y facturas
          </p>
        </div>
        <button 
          onClick={activeTab === 'pending' ? fetchAccountsPayable : fetchPaidInvoices} 
          className="btn-outline"
        >
          🔄 Actualizar
        </button>
      </div>

      {/* Tabs - VISIBLE VERSION */}
      <div className="bg-white rounded-lg shadow-md mb-6">
        <div className="flex border-b-2 border-gray-200">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-6 py-4 text-lg font-semibold border-b-3 ${
              activeTab === 'pending'
                ? 'border-blue-500 text-blue-600 bg-blue-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            📋 Facturas Pendientes
          </button>
          <button
            onClick={() => setActiveTab('paid')}
            className={`px-6 py-4 text-lg font-semibold border-b-3 ${
              activeTab === 'paid'
                ? 'border-green-500 text-green-600 bg-green-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            ✅ Facturas Pagadas
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="text-sm underline ml-2">
            Cerrar
          </button>
        </div>
      )}

      {/* Pending Tab Content */}
      {activeTab === 'pending' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="stat-card">
              <div className="stat-card-icon bg-red-500">💰</div>
              <div className="stat-card-value">{formatCurrency(summary.total_debt_usd)}</div>
              <div className="stat-card-label">Deuda Total</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-icon bg-orange-500">⚠️</div>
              <div className="stat-card-value">{summary.overdue_count}</div>
              <div className="stat-card-label">Facturas Vencidas</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-icon bg-blue-500">🏪</div>
              <div className="stat-card-value">{summary.stores_with_debt}</div>
              <div className="stat-card-label">Tiendas con Deudas</div>
            </div>
          </div>

          {/* Resumen de Deuda por Proveedor */}
          {suppliersSummary.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">📊 Resumen de Deuda por Proveedor</h3>
              </div>
              <div className="p-4">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Proveedor</th>
                      <th className="text-right">Deuda Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suppliersSummary.map((supplier, index) => (
                      <tr key={index}>
                        <td className="font-medium">{supplier.name}</td>
                        <td className="text-right font-semibold text-red-600">{formatCurrency(supplier.debt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">🔍 Filtros</h3>
              <button
                onClick={clearFilters}
                className="btn-outline text-sm"
              >
                Limpiar Filtros
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4">
              <div>
                <label className="form-label">Tienda</label>
                <select
                  value={selectedStore}
                  onChange={(e) => setSelectedStore(e.target.value)}
                  className="form-select"
                >
                  <option value="">Todas las tiendas</option>
                  {stores.map(store => (
                    <option key={store.id} value={store.id}>{store.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Proveedor</label>
                <select
                  value={selectedSupplier}
                  onChange={(e) => setSelectedSupplier(e.target.value)}
                  className="form-select"
                >
                  <option value="">Todos los proveedores</option>
                  {getUniqueSuppliers.map(supplier => (
                    <option key={supplier} value={supplier}>{supplier}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="form-label">N° Factura</label>
                <input
                  type="text"
                  value={searchPendingInvoice}
                  onChange={(e) => setSearchPendingInvoice(e.target.value)}
                  className="form-input"
                  placeholder="Buscar por número..."
                />
              </div>
              
              <div>
                <label className="form-label">Fecha Vencimiento Desde</label>
                <input
                  type="date"
                  value={startDate ? startDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                  className="form-input"
                />
              </div>
              
              <div>
                <label className="form-label">Fecha Vencimiento Hasta</label>
                <input
                  type="date"
                  value={endDate ? endDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                  className="form-input"
                  min={startDate ? startDate.toISOString().split('T')[0] : ''}
                />
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={fetchAccountsPayable}
                  className="btn-primary w-full"
                >
                  🔄 Actualizar
                </button>
              </div>
            </div>
          </div>

          {/* Facturas Pendientes - Vista Paginada */}
          {getPaginatedPendingData.totalItems === 0 ? (
            <div className="text-center py-12">
              {!selectedStore && !loadFullData ? (
                // Mensaje cuando no se ha seleccionado tienda ni cargado todo
                <>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    👋 Selecciona una tienda para comenzar
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Para ver las cuentas por pagar, selecciona una tienda del filtro arriba.
                  </p>
                  <button
                    onClick={() => {
                      setLoadFullData(true);
                      fetchAccountsPayable();
                    }}
                    className="btn-primary"
                  >
                    📊 O cargar todas las tiendas
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    (Cargar todas puede tardar unos segundos)
                  </p>
                </>
              ) : (
                // Mensaje cuando hay filtros pero no hay resultados
                <>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {selectedStore ? 'No se encontraron resultados' : 'No hay cuentas por pagar pendientes'}
                  </h3>
                  <p className="text-gray-600">
                    {selectedStore ? 
                      `No hay facturas pendientes para la tienda seleccionada con los filtros aplicados.` :
                      '¡Excelente! Todas las facturas están pagadas.'
                    }
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">📋 Facturas Pendientes ({getPaginatedPendingData.totalItems} total)</h3>
              </div>
              
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="w-12">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              selectAllVisible();
                            } else {
                              clearSelection();
                            }
                          }}
                          checked={getPaginatedPendingData.items.length > 0 && getPaginatedPendingData.items.every(inv => selectedInvoices.has(inv.id))}
                          className="cursor-pointer"
                        />
                      </th>
                      <th>Tienda</th>
                      <th>Proveedor</th>
                      <th>N° Factura</th>
                      <th>Monto Total</th>
                      <th>Pagado</th>
                      <th>Pendiente</th>
                      <th>Vencimiento</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedPendingData.items.map((invoice) => (
                      <tr key={invoice.id} className={selectedInvoices.has(invoice.id) ? 'bg-blue-50' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedInvoices.has(invoice.id)}
                            onChange={() => toggleInvoiceSelection(invoice.id)}
                            className="cursor-pointer"
                          />
                        </td>
                        <td>
                          <span className="text-sm font-medium">{invoice.storeName}</span>
                        </td>
                        <td>
                          <span className="text-sm font-medium">{invoice.supplierName}</span>
                        </td>
                        <td>
                          <span className="font-mono text-gray-900">
                            {invoice.invoice_number}
                          </span>
                        </td>
                        <td>
                          <span className="font-semibold text-gray-700">
                            {formatCurrency(invoice.total_amount)}
                          </span>
                        </td>
                        <td>
                          <span className="font-semibold text-green-600">
                            {formatCurrency(invoice.total_paid)}
                          </span>
                        </td>
                        <td>
                          <span className="font-semibold text-red-600">
                            {formatCurrency(invoice.remaining_debt)}
                          </span>
                        </td>
                        <td>
                          <span className="text-sm">
                            {formatDate(invoice.due_date)}
                          </span>
                        </td>
                        <td>
                          <span className={`text-sm ${getDaysUntilDueColor(invoice.days_until_due)}`}>
                            {getDaysUntilDueText(invoice.days_until_due)}
                          </span>
                        </td>
                        <td>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleOpenPaymentModal(invoice)}
                              className="btn-primary text-sm"
                            >
                              💳 Pagar
                            </button>
                            <button
                              onClick={() => handleViewPaymentHistory(invoice)}
                              className="btn-outline text-sm"
                            >
                              📋 Historial
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <PaginationControls 
                data={getPaginatedPendingData} 
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
              />
            </div>
          )}
        </>
      )}

      {/* Barra de selección múltiple - Solo visible cuando hay selecciones */}
      {selectedInvoices.size > 0 && activeTab === 'pending' && (
        <div className="fixed bottom-0 left-0 right-0 bg-blue-600 text-white p-4 shadow-lg z-50">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div>
                <span className="font-bold text-lg">{selectedTotals.count}</span>
                <span className="ml-2">facturas seleccionadas</span>
              </div>
              <div className="border-l border-blue-400 pl-6">
                <span className="text-sm">Total a pagar:</span>
                <span className="ml-2 font-bold text-xl">{formatCurrency(selectedTotals.totalUSD)}</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={clearSelection}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-400 rounded text-sm"
              >
                Cancelar Selección
              </button>
              <button
                onClick={() => setShowBulkPaymentModal(true)}
                className="px-6 py-2 bg-white text-blue-600 hover:bg-gray-100 rounded font-semibold"
              >
                💳 Pagar {selectedTotals.count} Factura{selectedTotals.count > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paid Tab Content */}
      {activeTab === 'paid' && (
        <>
          {/* Paid Invoices Filters */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">🔍 Filtros para Facturas Pagadas</h3>
              <button
                onClick={() => {
                  setPaidFilters({
                    storeId: '',
                    startDate: null,
                    endDate: null,
                    supplierName: '',
                    paymentMethod: '',
                    limit: 10000
                  });
                  setSearchPaidInvoice('');
                }}
                className="btn-outline text-sm"
              >
                Limpiar Filtros
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 p-4">
              <div>
                <label className="form-label">Tienda</label>
                <select
                  value={paidFilters.storeId}
                  onChange={(e) => setPaidFilters(prev => ({ ...prev, storeId: e.target.value }))}
                  className="form-select"
                >
                  <option value="">Todas las tiendas</option>
                  {stores.map(store => (
                    <option key={store.id} value={store.id}>{store.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="form-label">N° Factura</label>
                <input
                  type="text"
                  value={searchPaidInvoice}
                  onChange={(e) => setSearchPaidInvoice(e.target.value)}
                  className="form-input"
                  placeholder="Buscar por número..."
                />
              </div>
              
              <div>
                <label className="form-label">Fecha Pago Desde</label>
                <input
                  type="date"
                  value={paidFilters.startDate ? paidFilters.startDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setPaidFilters(prev => ({ ...prev, startDate: e.target.value ? new Date(e.target.value) : null }))}
                  className="form-input"
                />
              </div>
              
              <div>
                <label className="form-label">Fecha Pago Hasta</label>
                <input
                  type="date"
                  value={paidFilters.endDate ? paidFilters.endDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setPaidFilters(prev => ({ ...prev, endDate: e.target.value ? new Date(e.target.value) : null }))}
                  className="form-input"
                  min={paidFilters.startDate ? paidFilters.startDate.toISOString().split('T')[0] : ''}
                />
              </div>
              
              <div>
                <label className="form-label">Proveedor</label>
                <input
                  type="text"
                  value={paidFilters.supplierName}
                  onChange={(e) => setPaidFilters(prev => ({ ...prev, supplierName: e.target.value }))}
                  className="form-input"
                  placeholder="Nombre del proveedor"
                />
              </div>
              
              <div>
                <label className="form-label">Método de Pago</label>
                <select
                  value={paidFilters.paymentMethod}
                  onChange={(e) => setPaidFilters(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  className="form-select"
                >
                  <option value="">Todos los métodos</option>
                  <option value="transferencia">Transferencia Bancaria</option>
                  <option value="cheque">Cheque</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="tarjeta">Tarjeta de Crédito</option>
                </select>
              </div>
            </div>
          </div>

          {/* Paid Invoices List */}
          {loadingPaid ? (
            <div className="flex items-center justify-center h-64">
              <div className="loading-spinner"></div>
              <span className="ml-2 text-gray-600">Cargando facturas pagadas...</span>
            </div>
          ) : paidInvoices.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📄</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No se encontraron facturas pagadas
              </h3>
              <p className="text-gray-600">
                {Object.values(paidFilters).some(f => f) 
                  ? 'Intenta ajustar los filtros de búsqueda.' 
                  : 'No hay facturas completamente pagadas en el sistema.'}
              </p>
            </div>
          ) : (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">✅ Facturas Completamente Pagadas ({getPaginatedPaidInvoices.totalItems})</h3>
              </div>
              
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Tienda</th>
                      <th>Proveedor</th>
                      <th>N° Factura</th>
                      <th>Monto Total</th>
                      <th>Fecha Vencimiento</th>
                      <th>Total Pagos</th>
                      <th>Último Pago</th>
                      <th>Métodos de Pago</th>
                      <th>Documentos</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedPaidInvoices.items.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>
                          <div className="flex items-center space-x-2">
                            <span className="text-blue-600">🏪</span>
                            <span className="font-medium">{invoice.store_name}</span>
                          </div>
                        </td>
                        <td>
                          <span className="font-medium text-gray-900">{invoice.supplier}</span>
                        </td>
                        <td>
                          <span className="font-mono text-gray-700">{invoice.invoice_number}</span>
                        </td>
                        <td>
                          <span className="font-semibold text-gray-700">
                            {formatCurrency(invoice.total_amount_usd)}
                          </span>
                        </td>
                        <td>
                          <span className="text-sm">{formatDate(invoice.due_date)}</span>
                        </td>
                        <td>
                          <div className="text-center">
                            <div className="font-semibold text-green-600">
                              {invoice.payment_summary?.total_payments || 0} pago(s)
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatCurrency(invoice.payment_summary?.total_paid_usd || 0)}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="text-sm">
                            {invoice.payment_summary?.last_payment_date 
                              ? formatDate(invoice.payment_summary.last_payment_date) 
                              : 'N/A'}
                          </span>
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {invoice.payment_summary?.payment_methods?.map((method, idx) => (
                              <span key={idx} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                {method}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          {invoice.payment_summary?.documents_count > 0 ? (
                            <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                              📄 PDF ({invoice.payment_summary?.documents_count})
                            </div>
                          ) : (
                            <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                              📭 Sin documentos
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewPaymentHistory(invoice)}
                              className="btn-outline text-sm"
                            >
                              📋 Ver Pagos
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <PaginationControls 
                data={getPaginatedPaidInvoices} 
                onPageChange={setPaidCurrentPage}
                itemsPerPage={itemsPerPage}
              />
            </div>
          )}
        </>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div className="modal-overlay" onClick={handleClosePaymentModal}>
          <div className="modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">💳 Registrar Pago</h2>
              <button
                onClick={handleClosePaymentModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handlePaymentSubmit}>
              <div className="modal-body space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Información de la Factura</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-blue-700">N° Factura:</span>
                      <p className="text-blue-900 font-mono">{selectedInvoice.invoice_number}</p>
                    </div>
                    <div>
                      <span className="font-medium text-blue-700">Monto Total:</span>
                      <p className="text-blue-900 font-semibold">{formatCurrency(selectedInvoice.total_amount)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-blue-700">Ya Pagado:</span>
                      <p className="text-green-600 font-semibold">{formatCurrency(selectedInvoice.total_paid)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-blue-700">Pendiente:</span>
                      <p className="text-red-600 font-semibold">{formatCurrency(selectedInvoice.remaining_debt)}</p>
                    </div>
                  </div>
                </div>

                {/* Dual Currency Payment Fields */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-3">💰 Monto del Pago</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="form-group">
                      <label className="form-label">Tasa de Cambio (Bs. por $) *</label>
                      <input
                        type="number"
                        value={paymentForm.exchange_rate_used}
                        onChange={(e) => handleExchangeRateChange(e.target.value)}
                        className="form-input"
                        min="0"
                        step="0.01"
                        required
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Tasa del día del pago
                      </p>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Monto en USD ($) *</label>
                      <input
                        type="number"
                        value={paymentForm.payment_amount_usd}
                        onChange={(e) => handleUSDAmountChange(e.target.value)}
                        className="form-input"
                        min="0"
                        max={selectedInvoice.remaining_debt}
                        step="0.01"
                        required
                      />
                      <p className="text-xs text-green-600 mt-1">
                        Máximo: ${selectedInvoice.remaining_debt.toFixed(2)}
                      </p>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Monto en VES (Bs.) *</label>
                      <input
                        type="number"
                        value={paymentForm.payment_amount_ves}
                        onChange={(e) => handleVESAmountChange(e.target.value)}
                        className="form-input"
                        min="0"
                        step="0.01"
                        required
                      />
                      <p className="text-xs text-blue-600 mt-1">
                        Calculado automáticamente
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                    <div className="text-sm text-blue-900">
                      <div className="flex items-center mb-2">
                        <span className="text-blue-500 mr-2">💡</span>
                        <span className="font-medium">Cálculo automático</span>
                      </div>
                      <p className="text-xs">
                        El monto en Bs. se calcula automáticamente: <strong>USD × Tasa = VES</strong>
                      </p>
                      <p className="text-xs mt-1">
                        {paymentForm.payment_amount_usd && paymentForm.exchange_rate_used && (
                          <span className="font-mono">
                            ${parseFloat(paymentForm.payment_amount_usd || 0).toFixed(2)} × {parseFloat(paymentForm.exchange_rate_used || 0).toFixed(2)} = Bs. {parseFloat(paymentForm.payment_amount_ves || 0).toFixed(2)}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Fecha de Pago *</label>
                  <input
                    type="date"
                    value={paymentForm.payment_date ? paymentForm.payment_date.toISOString().split('T')[0] : ''}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value ? new Date(e.target.value) : new Date() }))}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Número de Referencia del Pago *</label>
                  <input
                    type="text"
                    value={paymentForm.payment_reference}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_reference: e.target.value }))}
                    className="form-input"
                    placeholder="Ej: REF-2024-001234"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Método de Pago</label>
                  <select
                    value={paymentForm.payment_method}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_method: e.target.value }))}
                    className="form-select"
                  >
                    <option value="">Seleccionar método</option>
                    <option value="transferencia">Transferencia Bancaria</option>
                    <option value="cheque">Cheque</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="tarjeta">Tarjeta de Crédito</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Comprobante de Pago (PDF)</label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="form-input"
                    accept=".pdf"
                  />
                  {selectedFile && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                      <span className="text-green-800">
                        ✅ Archivo seleccionado: {selectedFile.name} 
                        ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                      <button
                        type="button"
                        onClick={() => setSelectedFile(null)}
                        className="ml-2 text-red-600 hover:text-red-800"
                      >
                        ✕ Remover
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Adjunta el comprobante del pago realizado (solo PDF, máx. 10MB)
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">Notas</label>
                  <textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="form-textarea"
                    rows="3"
                    placeholder="Notas adicionales sobre el pago..."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={handleClosePaymentModal}
                  className="btn-outline"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary"
                >
                  {submitting ? (
                    <span className="flex items-center">
                      <div className="loading-spinner mr-2"></div>
                      Registrando Pago...
                    </span>
                  ) : (
                    'Registrar Pago'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Payment Modal */}
      {showBulkPaymentModal && selectedInvoices.size > 0 && (
        <div className="modal-overlay" onClick={() => setShowBulkPaymentModal(false)}>
          <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">💳 Pago en Lote - {selectedInvoices.size} Facturas</h2>
              <button
                onClick={() => setShowBulkPaymentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Summary */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-blue-900 mb-3">Resumen del Pago</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-blue-700">Facturas seleccionadas:</span>
                    <span className="font-semibold text-blue-900">{selectedInvoices.size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700">Total a pagar:</span>
                    <span className="font-bold text-lg text-blue-900">{formatCurrency(selectedTotals.totalUSD)}</span>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <div className="flex">
                  <div className="text-2xl mr-3">⚠️</div>
                  <div>
                    <h4 className="font-medium text-yellow-900 mb-1">Nota Importante</h4>
                    <p className="text-sm text-yellow-800">
                      Este pago se registrará como un pago único que se aplicará proporcionalmente a todas las facturas seleccionadas.
                      Cada factura quedará marcada como pagada completamente.
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Form */}
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!window.confirm(`¿Confirmar pago de ${formatCurrency(selectedTotals.totalUSD)} para ${selectedInvoices.size} facturas?`)) {
                  return;
                }

                try {
                  setSubmitting(true);

                  // Get all selected invoices
                  const invoicesToPay = [];
                  Object.values(accountsPayable || {}).forEach(storeData => {
                    Object.values(storeData.suppliers || {}).forEach(supplier => {
                      (supplier.invoices || []).forEach(invoice => {
                        if (selectedInvoices.has(invoice.id)) {
                          invoicesToPay.push(invoice);
                        }
                      });
                    });
                  });

                  console.log('[BULK PAYMENT] Starting bulk payment for', invoicesToPay.length, 'invoices');
                  console.log('[BULK PAYMENT] Form data:', paymentForm);
                  console.log('[BULK PAYMENT] Selected file:', selectedFile?.name);
                  console.log('[BULK PAYMENT] Exchange rate from context:', exchangeRate);

                  // Get exchange rate - try from context first, then fetch from API
                  let currentRate = exchangeRate?.usd_to_ves_rate;
                  
                  if (!currentRate || currentRate <= 0) {
                    console.log('[BULK PAYMENT] Exchange rate not available in context, fetching from API...');
                    try {
                      const rateResponse = await apiClient.get('/exchange-rates/latest');
                      currentRate = rateResponse.data?.usd_to_ves_rate;
                      console.log('[BULK PAYMENT] Fetched exchange rate from API:', currentRate);
                    } catch (rateErr) {
                      console.error('[BULK PAYMENT] Error fetching exchange rate:', rateErr);
                    }
                  }

                  // Final validation
                  if (!currentRate || currentRate <= 0) {
                    throw new Error('No hay tasa de cambio disponible. Por favor, configura una tasa de cambio en el sistema desde el módulo de reportes.');
                  }

                  console.log('[BULK PAYMENT] Using exchange rate:', currentRate);

                  // Construir lista de pagos para enviar en un solo request
                  const paymentsToSend = invoicesToPay.map(invoice => {
                    let vesAmount;
                    if (paymentForm.payment_amount_ves && parseFloat(paymentForm.payment_amount_ves) > 0) {
                      const totalVes = parseFloat(paymentForm.payment_amount_ves);
                      const proportion = invoice.remaining_debt / selectedTotals.totalUSD;
                      vesAmount = totalVes * proportion;
                    } else {
                      vesAmount = invoice.remaining_debt * currentRate;
                    }
                    return {
                      inventory_purchase_id: invoice.id,
                      payment_date: paymentForm.payment_date || new Date().toISOString().split('T')[0],
                      payment_amount_usd: parseFloat(invoice.remaining_debt),
                      payment_amount_ves: parseFloat(vesAmount),
                      exchange_rate_used: parseFloat(currentRate),
                      payment_method: paymentForm.payment_method || 'Transferencia VES',
                      payment_reference: paymentForm.reference || 'Sin referencia',
                      payment_reference2: paymentForm.reference2 || null,
                      payment_reference3: paymentForm.reference3 || null,
                      payment_reference4: paymentForm.reference4 || null,
                      notes: `Pago en lote - ${selectedInvoices.size} facturas`
                    };
                  });

                  const bulkResult = await createBulkPayment({ payments: paymentsToSend });
                  const successCount = bulkResult.success || 0;
                  console.log('[BULK PAYMENT] All payments completed. Success:', successCount);

                  // Refresh data and clear selection
                  await fetchAccountsPayable();
                  clearSelection();
                  setShowBulkPaymentModal(false);
                  setSelectedFile(null);
                  setPaymentForm({
                    payment_date: '',
                    payment_amount_ves: '',
                    payment_method: '',
                    reference: ''
                  });
                  alert(`✅ Se registraron ${successCount} pagos correctamente`);
                } catch (err) {
                  console.error('[BULK PAYMENT] FULL ERROR OBJECT:', err);
                  console.error('[BULK PAYMENT] Error type:', typeof err);
                  console.error('[BULK PAYMENT] Error keys:', Object.keys(err));
                  console.error('[BULK PAYMENT] Error response:', err.response);
                  console.error('[BULK PAYMENT] Error response data:', err.response?.data);
                  
                  // Better error message handling
                  let errorMessage = 'Error desconocido';
                  if (err.response?.data?.detail) {
                    if (typeof err.response.data.detail === 'string') {
                      errorMessage = err.response.data.detail;
                    } else {
                      errorMessage = JSON.stringify(err.response.data.detail);
                    }
                  } else if (err.message) {
                    errorMessage = err.message;
                  } else if (typeof err === 'string') {
                    errorMessage = err;
                  } else {
                    errorMessage = JSON.stringify(err);
                  }
                  
                  console.error('[BULK PAYMENT] Final error message:', errorMessage);
                  alert('Error al procesar el pago en lote:\n\n' + errorMessage + '\n\nRevisa la consola del navegador (F12) para más detalles.');
                } finally {
                  setSubmitting(false);
                }
              }}>
                <div className="space-y-4">
                  <div className="form-group">
                    <label className="form-label">Fecha de Pago *</label>
                    <input
                      type="date"
                      value={paymentForm.payment_date || new Date().toISOString().split('T')[0]}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Monto Total Pagado en BS</label>
                    <input
                      type="number"
                      step="0.01"
                      value={paymentForm.payment_amount_ves || ''}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_amount_ves: e.target.value }))}
                      className="form-input"
                      placeholder={`Equivalente aprox: Bs ${(selectedTotals.totalUSD * exchangeRate.usd_to_ves_rate).toFixed(2)}`}
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Se dividirá proporcionalmente entre las {selectedInvoices.size} facturas
                    </p>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Método de Pago *</label>
                    <select
                      value={paymentForm.payment_method}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_method: e.target.value }))}
                      className="form-select"
                      required
                    >
                      <option value="">Seleccionar método</option>
                      <option value="Efectivo USD">Efectivo USD</option>
                      <option value="Efectivo VES">Efectivo VES</option>
                      <option value="Transferencia USD">Transferencia USD</option>
                      <option value="Transferencia VES">Transferencia VES</option>
                      <option value="Pago Móvil">Pago Móvil</option>
                      <option value="Zelle">Zelle</option>
                      <option value="Cheque">Cheque</option>
                      <option value="Tarjeta">Tarjeta</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Referencia de Pago 1</label>
                    <input
                      type="text"
                      value={paymentForm.reference || ''}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
                      className="form-input"
                      placeholder="Ej: 123456789"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Referencia de Pago 2 (opcional)</label>
                    <input
                      type="text"
                      value={paymentForm.reference2 || ''}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, reference2: e.target.value }))}
                      className="form-input"
                      placeholder="Segunda transferencia"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Referencia de Pago 3 (opcional)</label>
                    <input
                      type="text"
                      value={paymentForm.reference3 || ''}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, reference3: e.target.value }))}
                      className="form-input"
                      placeholder="Tercera transferencia"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Referencia de Pago 4 (opcional)</label>
                    <input
                      type="text"
                      value={paymentForm.reference4 || ''}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, reference4: e.target.value }))}
                      className="form-input"
                      placeholder="Cuarta transferencia"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Adjuntar Comprobante de Pago (PDF)</label>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      className="form-input"
                    />
                    {selectedFile && (
                      <p className="text-sm text-green-600 mt-1">
                        ✓ {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-1">
                      El mismo documento se adjuntará a todas las facturas seleccionadas
                    </p>
                  </div>
                </div>

                <div className="modal-footer mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowBulkPaymentModal(false);
                      setSelectedFile(null);
                    }}
                    className="btn-outline"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary"
                  >
                    {submitting ? 'Procesando...' : `💰 Confirmar Pago de ${formatCurrency(selectedTotals.totalUSD)}`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showPaymentHistory && selectedInvoice && (
        <div className="modal-overlay" onClick={() => setShowPaymentHistory(false)}>
          <div className="modal-content max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">📋 Historial de Pagos</h2>
              <button
                onClick={() => setShowPaymentHistory(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Invoice Info */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-blue-900 mb-2">Información de la Factura</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-blue-700">N° Factura:</span>
                    <p className="text-blue-900 font-mono">{selectedInvoice.invoice_number}</p>
                  </div>
                  <div>
                    <span className="font-medium text-blue-700">Monto Total:</span>
                    <p className="text-blue-900 font-semibold">{formatCurrency(selectedInvoice.total_amount)}</p>
                  </div>
                  <div>
                    <span className="font-medium text-blue-700">Total Pagado:</span>
                    <p className="text-green-600 font-semibold">{formatCurrency(selectedInvoice.total_paid)}</p>
                  </div>
                  <div>
                    <span className="font-medium text-blue-700">Pendiente:</span>
                    <p className="text-red-600 font-semibold">{formatCurrency(selectedInvoice.remaining_debt)}</p>
                  </div>
                </div>
              </div>

              {/* Payment History */}
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="loading-spinner"></div>
                  <span className="ml-2 text-gray-600">Cargando historial...</span>
                </div>
              ) : paymentHistory.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">📝</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No hay pagos registrados
                  </h3>
                  <p className="text-gray-600">
                    Esta factura aún no tiene pagos registrados.
                  </p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Monto USD</th>
                        <th>Monto VES</th>
                        <th>Referencia</th>
                        <th>Método</th>
                        <th>Documento</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.map((payment) => (
                        <tr key={payment.id}>
                          <td>
                            <span className="text-sm">
                              {formatDate(payment.payment_date)}
                            </span>
                          </td>
                          <td>
                            <span className="font-semibold text-green-600">
                              ${payment.payment_amount_usd?.toFixed(2) || '0.00'}
                            </span>
                          </td>
                          <td>
                            <span className="font-semibold text-green-600">
                              Bs. {payment.payment_amount_ves?.toFixed(2) || '0.00'}
                            </span>
                          </td>
                          <td>
                            <span className="font-mono text-sm">
                              {payment.payment_reference || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <span className="text-sm">
                              {payment.payment_method || 'N/A'}
                            </span>
                          </td>
                          <td>
                            {payment.payment_document_url ? (
                              <div className="space-y-2">
                                {/* Document Indicator Badge */}
                                <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                  📄 PDF (1)
                                </div>
                                
                                {/* Action Buttons */}
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleViewPdf(
                                      payment.id, 
                                      payment.payment_document_filename || `payment_${payment.id}.pdf`
                                    )}
                                    className="text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700"
                                    title="Ver documento PDF"
                                  >
                                    👁️ Ver
                                  </button>
                                  <button
                                    onClick={() => handleDownloadDocument(
                                      payment.id, 
                                      payment.payment_document_filename || `payment_${payment.id}.pdf`
                                    )}
                                    className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                                    title="Descargar documento PDF"
                                  >
                                    📥 Descargar
                                  </button>
                                  <button
                                    onClick={() => handleDeleteDocument(payment.id)}
                                    className="text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                                    title="Eliminar documento PDF"
                                  >
                                    🗑️ Eliminar
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                📭 Sin documento
                              </div>
                            )}
                          </td>
                          <td>
                            <button
                              onClick={async () => {
                                if (window.confirm('¿Estás seguro de que deseas eliminar este pago?')) {
                                  try {
                                    await deletePaymentRecord(payment.id);
                                    await handleViewPaymentHistory(selectedInvoice);
                                    await fetchAccountsPayable();
                                    alert('Pago eliminado correctamente');
                                  } catch (err) {
                                    console.error('Error deleting payment:', err);
                                    alert('Error al eliminar el pago');
                                  }
                                }
                              }}
                              className="btn-outline text-xs text-red-600 hover:text-red-800"
                            >
                              🗑️ Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowPaymentHistory(false)}
                className="btn-outline"
              >
                Cerrar Historial
              </button>
              <button
                onClick={() => {
                  setShowPaymentHistory(false);
                  handleOpenPaymentModal(selectedInvoice);
                }}
                className="btn-primary"
              >
                💳 Registrar Nuevo Pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Note: PDF Viewer now opens in new tab for better compatibility */}
    </div>
  );
};

export default AccountsPayable;